'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import Image from 'next/image';

const Footer: React.FC = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentYear, setCurrentYear] = useState('2026');

  useEffect(() => {
    setIsHydrated(true);
    setCurrentYear(new Date().getFullYear().toString());
  }, []);

  if (!isHydrated) {
    return (
      <footer className="bg-foreground text-white py-12">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <p className="text-sm opacity-80">Loading...</p>
          </div>
        </div>
      </footer>
    );
  }

  return (
    <footer className="bg-foreground text-white py-12 lg:py-16">
      <div className="container mx-auto px-4">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {/* About Section */}
          <div>
            <Link href="/" className="flex items-center space-x-3 mb-4">
              <Image
                src="/assets/images/logo.png"
                alt="seva setu logo"
                width={40}
                height={40}
              />
              <div>
                <h3 className="text-lg font-heading font-bold">Seva Setu Trust</h3>
                <p className="text-xs opacity-80">सेवा ए परमो धर्म</p>
              </div>
            </Link>
            <p className="text-sm opacity-80 leading-relaxed">
              A modern charitable trust making social service accessible, transparent, and impactful through technology-enabled community care.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-heading font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-2">
              <li>
                <Link href="/" className="text-sm opacity-80 hover:opacity-100 hover:text-primary transition-all duration-300">
                  Home
                </Link>
              </li>
              <li>
                <Link href="/about" className="text-sm opacity-80 hover:opacity-100 hover:text-primary transition-all duration-300">
                  About Us
                </Link>
              </li>
              <li>
                <Link href="/services" className="text-sm opacity-80 hover:opacity-100 hover:text-primary transition-all duration-300">
                  Services
                </Link>
              </li>
              <li>
                <Link href="/events" className="text-sm opacity-80 hover:opacity-100 hover:text-primary transition-all duration-300">
                  Events
                </Link>
              </li>
              <li>
                <Link href="/gallery" className="text-sm opacity-80 hover:opacity-100 hover:text-primary transition-all duration-300">
                  Gallery
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-heading font-semibold mb-4">Contact Us</h4>
            <ul className="space-y-3">
              <li className="flex items-start space-x-2">
                <Icon name="PhoneIcon" size={18} className="text-secondary mt-0.5 flex-shrink-0" />
                <span className="text-sm opacity-80">+91 87587 58778</span>
              </li>
              <li className="flex items-start space-x-2">
                <Icon name="EnvelopeIcon" size={18} className="text-accent mt-0.5 flex-shrink-0" />
                <span className="text-sm opacity-80">info@sevasetutrust.org</span>
              </li>
              <li className="flex items-start space-x-2">
                <Icon name="MapPinIcon" size={18} className="text-primary mt-0.5 flex-shrink-0" />
                <span className="text-sm opacity-80">Sarthana, Surat, Gujarat</span>
              </li>
            </ul>
          </div>

          {/* Social Links */}
          <div>
            <h4 className="text-lg font-heading font-semibold mb-4">Follow Us</h4>
            <div className="flex space-x-3">
              <a
                href="https://facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-primary transition-all duration-300"
              >
                <Icon name="ShareIcon" size={20} />
              </a>
              <a
                href="https://twitter.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-primary transition-all duration-300"
              >
                <Icon name="ShareIcon" size={20} />
              </a>
              <a
                href="https://instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-primary transition-all duration-300"
              >
                <Icon name="ShareIcon" size={20} />
              </a>
            </div>
          </div>
        </div>

        <div className="border-t border-white/20 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-sm opacity-80">
              &copy; {currentYear} Seva Setu Trust. All rights reserved.
            </p>
            <div className="flex space-x-6">
              <Link href="#" className="text-sm opacity-80 hover:opacity-100 transition-opacity duration-300">
                Privacy Policy
              </Link>
              <Link href="#" className="text-sm opacity-80 hover:opacity-100 transition-opacity duration-300">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;